// Detalle de una observación del hilo (#0571, ADR-0105/ADR-0129; handoff
// «detalle de observación» v2). Nació para postventa; desde ADR-0134
// (Fase 4) es el detalle de TODA receta con `observationStatuses`, y
// facility lo usa también: la delegación (DetalleObservacion.tsx) se
// retiró. Lo que difiere entre verticales sale de la receta —etiquetas de
// estado, nombres de los carriles, si hay taxonomía, quién cierra—, nunca
// del slug.
//
// Dos direcciones: /proyectos/:id/observaciones/:obsId cuando la vertical
// navega por proyecto (`hasContextualProjectNav`, postventa) y
// /observaciones/:id para el resto (facility), que es donde vuelve.
//
// Tres piezas (README §Overview):
//   1. Card de la observación (`ObservacionCard`, `.obs-detail`): estado,
//      «Reabierta ×N», título, descripción, recinto, especialidad,
//      criticidad y evidencia inicial — la misma card que ven el
//      propietario y el contratista.
//   2. Historial de gestión: el hilo, más reciente arriba.
//   3. Panel de gestión (`GestionPanel`, aside): acciones según el hito.
//
// Reglas:
//   - El ESTADO que se pinta sale de `presentationalState` (fase del
//     engine + overlay de cierre → `estadoKindFor` del módulo compartido
//     de etiquetas). Acá no se compara ninguna clave de estado ni el ciclo
//     contra un número (fitness observation-phase-single-source).
//   - Las ACCIONES salen de `resolveGestionActions` (grafo de la receta
//     para el carril del usuario); esta pantalla JAMÁS re-encodea una
//     transición (ADR-0105 §Consequences).
//   - Sin panel de IA: su recomendación prellena las instrucciones al
//     contratista. «Corregir clasificación» queda deshabilitado (destino
//     pendiente); ClassificationCandidates / ClassifyFindingModal siguen
//     en el repo, sin montar acá.
//   - Voz = SIEMPRE texto, jamás un <audio> (regla dura del handoff).

import { useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  appealedObservationKeys,
  decodeVisitRequestNote,
  especialidadLabel,
  nonGraphTimelineLabel,
  OBSERVATION_AUTO_CLOSED_LABEL,
  observationPhase,
  observationVisitsEnabled,
  visitReasonLabelForAsset,
  type FindingDetailEvidence,
  type FindingDetailResponse,
  type FindingTimelineEntry,
  type ObservationPhaseVocabulary,
  type OrgMember,
} from "@bitacorapro/api-types";
import { GestionPanel, type GestionContext } from "../components/GestionPanel";
import { Icon } from "../components/icons";
import { ObservacionCard } from "../components/ObservacionCard";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { PhotoGallery } from "../components/PhotoGallery";
import { Skel } from "../components/Skel";
import { ApiError } from "../lib/api/error";
import { useFinding, useMemberships, useRecipe } from "../lib/api/queries";
import { useSession } from "../lib/auth/useSession";
import { isFacilityDashboardEnabled } from "../lib/capabilities";
import { reabiertaTrasCierre } from "../lib/postventa/closure";
import { useQueryView } from "../lib/loading/useQueryView";
import { hasContextualProjectNav } from "../lib/nav";
import { focusHeading } from "../lib/useDialogFocus";
import {
  assignFlavor,
  resolveAppealActions,
  resolveGestionActions,
  resolveVerdictActions,
} from "../lib/postventa/actions";
import { descripcionesDe } from "../lib/postventa/ownerWords";
import { presentationalState } from "../lib/postventa/phase";
import { resolveSeverityCode } from "../lib/postventa/severity";
import { laneRoleLabels, threadWords, type ThreadWords } from "../lib/postventa/words";
import {
  describeTimelineActor,
  lastSeenLabel,
  observationActorLane,
  storeTitle,
} from "../lib/util";
import {
  isOwnerAuthoredIntake,
  observationEntryStatus,
  observationPhaseKeys,
  type ObservationStatusActor,
} from "@bitacorapro/core-engine";
// ADR-0156 — la marca del aviso inmediato por observación crítica.
import { avisoCriticoLabel } from "../lib/postventa/avisoCritico";
// ADR-0163 — la observación que nació de una consulta del propietario.
import { DesdeLaConsulta } from "../components/DesdeLaConsulta";
import { gateOf, RefreshNotices, SlowNotice } from "./queryViewKit";

/** Título corto: el de la IA, o la primera frase de la descripción
 *  recortada a 80 caracteres. */
function tituloDe(o: FindingDetailResponse["observation"]): string {
  if (o.aiTitle) return o.aiTitle;
  const src = o.aiDescription ?? o.humanDescription ?? o.element ?? "Observación";
  const frase = src.split(/(?<=[.!?])\s/)[0] ?? src;
  return frase.length > 80 ? `${frase.slice(0, 79).trimEnd()}…` : frase;
}

export function DetalleObservacionPostventa() {
  // Dos montajes (panel v2, README §Navegación): el canónico
  // /proyectos/:id/observaciones/:obsId, donde `id` es el proyecto, y el
  // legado /observaciones/:id, que ObservacionDetailRoute sigue sirviendo
  // (y redirige al canónico cuando la observación tiene proyecto).
  const params = useParams<{ id: string; obsId?: string }>();
  const findingId = params.obsId ?? params.id;
  const routeGroupId = params.obsId ? params.id : undefined;
  const findingQuery = useFinding(findingId);
  const recipeQuery = useRecipe();
  const { data } = findingQuery;
  const maybeRecipe = recipeQuery.data;
  // La observación y la receta son independientes y la pantalla necesita
  // las dos: el contrato las pliega en una vista. Sin id el detalle queda
  // deshabilitado → «ya no existe», nunca un skeleton eterno ni una
  // página en blanco.
  const findingView = useQueryView(findingQuery);
  const recipeView = useQueryView(recipeQuery);
  const gate = gateOf([findingView, recipeView] as const);
  const retryLoad = () => {
    for (const q of [findingQuery, recipeQuery]) if (q.isPending || q.isError) void q.refetch();
  };
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  // El listado de vuelta: el del proyecto cuando la vertical navega por
  // proyecto (postventa); si no, la lista de la org (facility, cuyo
  // `assetGroupId` es una zona, no un proyecto con su propia lista). La
  // dirección canónica bajo el proyecto ya lo trae en la ruta.
  const projectNav = hasContextualProjectNav(maybeRecipe);
  const groupId = routeGroupId ?? (projectNav ? (data?.asset.assetGroupId ?? null) : null);
  const listPath = groupId ? `/proyectos/${groupId}/observaciones` : "/observaciones";

  // El nombre del propietario lo resuelve el servidor en el propio
  // detalle (`asset.ownerLabel`).
  const propietario = data?.asset.ownerLabel ?? null;

  const membershipsQuery = useMemberships();
  const rosterByUserId = useMemo(
    () =>
      new Map<string, Pick<OrgMember, "displayName" | "role">>(
        (membershipsQuery.data?.members ?? []).map((m) => [m.userId, m])
      ),
    [membershipsQuery.data]
  );

  // El foco va al título cuando el gate deja el skeleton, no cuando llega
  // `data`: con el skeleton pintado el contrato lo sostiene (fase de
  // retención) y el <h1> aún no existe en el render donde llegan los datos.
  const titleRef = useRef<HTMLHeadingElement>(null);
  const ready = gate.kind === "ready";
  useEffect(() => {
    if (ready) focusHeading(titleRef);
  }, [ready, findingId]);

  const lane = observationActorLane(maybeRecipe, session?.organization.role);

  function volver() {
    // The first entry of a history has no previous page to go back to
    // (a pasted link): fall back to the project's list.
    if (location.key === "default") navigate(listPath);
    else navigate(-1);
  }

  const header = (
    <div className="page-header" style={{ marginBottom: 20 }}>
      <div className="page-header__lead">
        <div className="page-header__eyebrow">
          <Link to={listPath}>Observaciones</Link>
        </div>
      </div>
    </div>
  );

  const yaNoExiste = (
    <div className="page-pad">
      <h1 className="page-header__title">Esta observación ya no existe</h1>
      <p className="page-header__sub">
        Puede haber sido eliminada, o el enlace que seguiste quedó desactualizado.
      </p>
      <Link to={listPath} className="btn btn--secondary">
        <Icon.Arrow s={14} /> Volver a observaciones
      </Link>
    </div>
  );

  if (gate.kind === "error") {
    if (gate.error instanceof ApiError && gate.error.status === 404) return yaNoExiste;
    return (
      <div className="page">
        {header}
        <div className="panel">
          {gate.offline ? (
            <PageError
              title="Sin conexión"
              description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
              onRetry={gate.retry}
            />
          ) : (
            <PageError
              title="No pudimos cargar esta observación"
              description="Puede ser un problema de conexión momentáneo. Tus otras observaciones siguen disponibles."
              onRetry={gate.retry}
            />
          )}
        </div>
      </div>
    );
  }

  if (gate.kind === "loading") {
    return (
      <div className="page">
        {header}
        <PageSkeleton label="Cargando observación…" visible={gate.visible}>
          <div className="thread">
            <div className="thread__main">
              <div className="obs-detail">
                <div className="obs-detail__head">
                  <Skel width={72} height={22} pill />
                  <Skel width={88} height={22} pill />
                </div>
                <Skel width="70%" height={24} />
                <div className="skel-stack" style={{ marginTop: 12 }}>
                  <Skel width="90%" height={12} />
                  <Skel width="60%" height={12} />
                </div>
                <div className="obs-detail__pills" style={{ marginTop: 14 }}>
                  <Skel width={80} height={22} pill />
                  <Skel width={110} height={22} pill />
                  <Skel width={72} height={22} pill />
                </div>
                <div className="obs-detail__ev">
                  <div className="evc__thumbs" aria-hidden="true">
                    {[0, 1, 2, 3].map((i) => (
                      <Skel key={i} width={72} height={72} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <aside className="thread__aside">
              <div className="panel">
                <div className="skel-stack">
                  <Skel width="80%" height={12} />
                  <Skel width="50%" height={12} />
                  <Skel width="70%" height={12} />
                </div>
                <div className="g-body">
                  <Skel width="100%" height={80} />
                </div>
              </div>
            </aside>
          </div>
        </PageSkeleton>
        {gate.slow && <SlowNotice onRetry={retryLoad} />}
      </div>
    );
  }

  if (gate.kind === "disabled") return yaNoExiste;

  const [findingData, recipe] = gate.data;
  const { observation: o, asset, evidence, timeline } = findingData;
  const vocab: ObservationPhaseVocabulary = recipe;
  const state = presentationalState(o, vocab);
  const actions = resolveGestionActions(recipe, o.observationStatus, lane);
  const appeal = resolveAppealActions(recipe, o.observationStatus, lane);
  const words = threadWords(recipe);
  const ctx = gestionContext(recipe, words, lane, storeTitle(asset, "") || null);
  const sev = resolveSeverityCode(o);
  const titulo = tituloDe(o);
  const unidad = asset.unit;
  const recinto = o.recintoNombre ?? (o.areaName === "General" ? null : o.areaName);
  const especialidad = especialidadLabel(recipe.findingCategories, o.category);
  const contratista = o.provider?.shortName ?? null;
  // ADR-0163 — también la consulta convertida: la contó el propietario.
  const delPropietario = isOwnerAuthoredIntake(o.origen ?? null);
  // Facility: el objeto es una tienda con su propia ficha (/tiendas/:id).
  const assetLink = isFacilityDashboardEnabled(recipe)
    ? { to: `/tiendas/${asset.id}`, label: storeTitle(asset) }
    : unidad
      ? { to: `${listPath}?unidad=${asset.id}`, label: `${words.asset} ${unidad}` }
      : null;

  // Evidencia por evento (#0454): las fotos de un hito cuelgan de SU
  // entrada del hilo; lo que nació con la observación (findingEventId
  // null) es la evidencia inicial de la card.
  const evidenceByEvent = new Map<string, FindingDetailEvidence[]>();
  for (const e of evidence) {
    if (!e.findingEventId) continue;
    const list = evidenceByEvent.get(e.findingEventId) ?? [];
    list.push(e);
    evidenceByEvent.set(e.findingEventId, list);
  }
  const fotos = evidence
    .filter((e) => e.evidenceType === "photo" && !e.findingEventId)
    .map((e) => ({ url: e.url, caption: e.caption ?? null }));

  return (
    <div className="page">
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div className="page-header__lead">
          <div className="page-header__eyebrow">
            <Link to={listPath}>Observaciones</Link>
            {" / "}
            {assetLink ? (
              <Link to={assetLink.to}>{assetLink.label}</Link>
            ) : (
              (asset.address ?? words.asset)
            )}
            {recinto ? ` · ${recinto}` : ""}
          </div>
        </div>
        <div className="page-header__actions">
          <button type="button" className="btn btn--secondary btn--sm" onClick={volver}>
            <Icon.Arrow s={14} /> Volver
          </button>
        </div>
      </div>

      <RefreshNotices
        refreshFailed={gate.refreshFailed}
        revalidatingHint={gate.revalidatingHint}
        onRetry={retryLoad}
      />

      <div className="thread">
        <div className="thread__main thread__main--stack">
          <ObservacionCard
            titleRef={titleRef}
            titulo={titulo}
            descripcion={o.aiDescription ?? o.humanDescription ?? null}
            fecha={o.observedAt ?? o.createdAt}
            state={state}
            recinto={recinto}
            especialidad={especialidad}
            sev={sev}
            sevLegacy={sev === null ? o.severity : null}
            fotos={fotos}
            descripciones={descripcionesDe(o)}
            autor={delPropietario ? propietario : null}
            rol={
              delPropietario
                ? words.assetParty.toLowerCase()
                : recipe.labels.organization.toLowerCase()
            }
            avisoCritico={avisoCriticoLabel(o.criticalAlert)}
            fotosEnConsulta={o.sourceInquiry?.attachmentIds.length ?? 0}
          />

          {o.sourceInquiry && asset.assetGroupId && (
            <DesdeLaConsulta
              assetGroupId={asset.assetGroupId}
              inquiryId={o.sourceInquiry.inquiryId}
              attachmentIds={o.sourceInquiry.attachmentIds}
            />
          )}

          <section aria-labelledby="obs-historial">
            <div className="drawer__section-label" id="obs-historial">
              Historial de gestión
            </div>
            {timeline.length === 0 ? (
              <p className="cell-sub cell-sub--italic">Sin eventos registrados todavía.</p>
            ) : (
              <ol className="hilo">
                {describeTimeline(timeline, {
                  vocab,
                  propietario,
                  contratista,
                  selfUserId: session?.user.id ?? null,
                  selfRole: session?.organization.role ?? null,
                  rosterByUserId,
                  fotosDeNacimiento: fotos.length,
                  words,
                  roleLabel: (key) => recipe.memberRoles.find((r) => r.key === key)?.label ?? null,
                  laneLabel: (l) => laneRoleLabels(recipe, l)[0] ?? null,
                })
                  .reverse()
                  .map((ev) => (
                    <li key={ev.id} className={`ev ${ev.tone}`}>
                      <span className="ev__dot" aria-hidden="true" />
                      <div className="ev__card">
                        <div className="ev__head">
                          <span className="ev__author">{ev.author}</span>
                          <span className={`role-pill ${ev.roleClass}`}>{ev.role}</span>
                          <time className="ev__time" dateTime={ev.occurredAt}>
                            {lastSeenLabel(ev.occurredAt)}
                          </time>
                        </div>
                        <div className="ev__kind">{ev.kind}</div>
                        {ev.text && <p className="ev__text">{ev.text}</p>}
                        {(evidenceByEvent.get(ev.id) ?? []).some(
                          (e) => e.evidenceType === "photo"
                        ) && (
                          <div className="ev__shots">
                            {/* Evidencia del hito (#0454): cuelga de ESTA
                                entrada, no de la pila de la observación. */}
                            <PhotoGallery
                              photos={(evidenceByEvent.get(ev.id) ?? [])
                                .filter((e) => e.evidenceType === "photo")
                                .map((e) => ({
                                  id: e.id,
                                  url: e.url,
                                  areaTag: null,
                                  caption: e.caption ?? null,
                                  capturedAt: e.createdAt,
                                  conformity: null,
                                  verdict: null,
                                  itemLabel: null,
                                  itemConformance: null,
                                  review: null,
                                }))}
                            />
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
              </ol>
            )}
          </section>
        </div>

        <aside className="thread__aside" aria-label="Gestión de la observación">
          <GestionPanel
            observation={o}
            state={state}
            actions={actions}
            appeal={appeal}
            propietario={propietario}
            ctx={ctx}
            reabiertaTrasCierre={reabiertaTrasCierre(timeline)}
            visita={{
              enabled: observationVisitsEnabled(recipe),
              schedulable: findingData.visitSchedulable === true,
              request: findingData.visitRequest ?? null,
              contact: findingData.asset.visitContact ?? null,
              proposal: findingData.visitProposal ?? null,
              history: findingData.visitHistory ?? [],
            }}
          />
        </aside>
      </div>
    </div>
  );
}

// Lo que el panel lee de la receta, resuelto una vez acá.
function gestionContext(
  recipe: NonNullable<ReturnType<typeof useRecipe>["data"]>,
  words: ThreadWords,
  lane: ReturnType<typeof observationActorLane>,
  assetTitle: string | null
): GestionContext {
  const keys = observationPhaseKeys(recipe);
  const labelOf = (key: string | undefined) =>
    recipe.observationStatuses.find((s) => s.key === key)?.label ?? null;
  const vocab: ObservationPhaseVocabulary = recipe;
  const resolvedKey = keys.awaiting_receiver[0];
  // ¿Quién llega a resuelta? Si sólo el enlace, el panel puede decir «el
  // contratista marcó…»; si también otro carril (el jefe de tienda que lo
  // arregla él mismo, ADR-0134), no.
  const resolvers = new Set(
    recipe.observationStatuses.flatMap((s) =>
      s.transitionsTo.filter((t) => t.to === resolvedKey).flatMap((t) => t.actors)
    )
  );
  return {
    words,
    flavor: assignFlavor(recipe),
    verdict: resolveVerdictActions(recipe, lane),
    entryLabel: presentationalState({ observationStatus: null }, vocab).label,
    resolvedLabel: labelOf(resolvedKey) ?? "Resuelta",
    onlyAssigneeResolves: resolvers.size === 1 && resolvers.has("share_link"),
    assetTitle,
    archivoDe: hasContextualProjectNav(recipe)
      ? ` del ${recipe.labels.assetGroup.toLowerCase()}`
      : "",
  };
}

// ---------------------------------------------------------------------------
// Historial — cada hito del hilo traducido a autor / carril / tipo.
//
// El TIPO del hito sale de la fase del estado de destino según el engine
// (`observationPhase` sobre `toStatus`), nunca de comparar la clave: así
// «Aceptó» es «pasó a la fase de espera de asignación», «Asignó» es
// «pasó a la fase con contratista», etc. Los hitos del propietario
// (conformidad / no conformidad) tienen su propio eventType.

type TimelineRow = {
  id: string;
  occurredAt: string;
  author: string;
  role: string;
  roleClass: string;
  kind: string;
  text: string | null;
  tone: string;
};

function describeTimeline(
  timeline: readonly FindingTimelineEntry[],
  ctx: {
    vocab: ObservationPhaseVocabulary;
    propietario: string | null;
    contratista: string | null;
    selfUserId: string | null;
    selfRole: string | null;
    rosterByUserId: ReadonlyMap<string, Pick<OrgMember, "displayName" | "role">>;
    fotosDeNacimiento: number;
    words: ThreadWords;
    /** El `label` de la receta para una clave de rol. */
    roleLabel: (roleKey: string) => string | null;
    /** El `label` del primer rol vivo que declara el carril. */
    laneLabel: (lane: ObservationStatusActor) => string | null;
  }
): TimelineRow[] {
  const phaseOf = (status: string | null) =>
    status === null
      ? null
      : observationPhase(ctx.vocab, {
          observationStatus: status,
          observationClosedAt: null,
          observationCycle: null,
        }).phase;
  const w = ctx.words;
  // La entrada del grafo: triage (postventa) o la espera de asignación
  // (facility, sin triage). Un hito que vuelve ahí desde lo resuelto es
  // una No conformidad.
  const entryPhase = phaseOf(presentationalStateEntryKey(ctx.vocab));
  // ADR-0141: los estados apelados, para rotular la resolución de una
  // apelación por su ORIGEN.
  const apeladas = appealedObservationKeys(ctx.vocab);

  let ciclo = 1;
  return timeline.map((ev) => {
    // El carril que estampó el servidor (`finding_events.actor_lane`,
    // ADR-0134) manda; un hito anterior a la columna cae a la forma del
    // actor.
    const lane =
      ev.actorLane ??
      (ev.actor.kind === "share_link"
        ? "share_link"
        : ev.actor.kind === "system"
          ? "system"
          : "session_user");
    const userId = ev.actor.kind === "user" ? ev.actor.userId : null;
    const isSelf = userId !== null && userId === ctx.selfUserId;
    // El rol del autor, por la receta: su membresía si la conocemos (el
    // supervisor y el gerente de facility comparten carril), si no el
    // primer rol que declara el carril.
    const memberRole = isSelf ? ctx.selfRole : userId ? ctx.rosterByUserId.get(userId)?.role : null;
    const roleOfUser = memberRole ? ctx.roleLabel(memberRole) : null;
    const who =
      lane === "asset_party"
        ? userId && !isSelf && ctx.rosterByUserId.has(userId)
          ? describeTimelineActor(ev.actor, ctx.rosterByUserId)
          : isSelf
            ? "Tú"
            : (ctx.propietario ?? w.assetParty)
        : lane === "share_link"
          ? (ev.provider?.shortName ?? ctx.contratista ?? w.assignee)
          : lane === "system"
            ? "Sistema"
            : isSelf
              ? "Tú"
              : describeTimelineActor(ev.actor, ctx.rosterByUserId);
    const [role, roleClass] =
      lane === "asset_party"
        ? [roleOfUser ?? w.assetParty, ""]
        : lane === "share_link"
          ? [w.assignee, "role-pill--ext"]
          : lane === "system"
            ? ["Sistema", ""]
            : [
                roleOfUser ??
                  ctx.laneLabel(lane === "session_user" ? "assigning_party" : lane) ??
                  "Equipo",
                "role-pill--sup",
              ];

    let kind: string;
    let tone = "ev--open";
    let text: string | null = ev.note;
    const asignadoDelHito = ev.provider?.shortName ?? ctx.contratista ?? `el ${w.assigneeLower}`;
    // Hitos fuera del grafo del hilo (api-types `nonGraphTimelineLabel`):
    // el sintético de la migración de facility y el historial de la
    // delegación retirada. Su `toStatus` es el estado en que quedó tras
    // migrar o una clave de la delegación; por la rama de fase leerían
    // «Asignó…», «Anuló…», «Marcó como resuelta» o «Cambió el estado».
    const fueraDelGrafo = nonGraphTimelineLabel(ev);

    // El hito sintético del nacimiento (`synthetic`, `toStatus: null`)
    // narra el reporte, no un cambio de estado: se resuelve acá, antes de
    // la rama por fase de `toStatus`, que para él no tiene nada que leer.
    if (fueraDelGrafo?.kind === "reopened") {
      // ADR-0142 §4 — el propietario reabrió un cierre por falta de
      // respuesta: abre un ciclo nuevo, como un «No conforme», pero no lo es.
      ciclo += 1;
      kind = `${fueraDelGrafo.label} · se abre el ciclo ${ciclo}`;
      tone = "ev--crit";
    } else if (fueraDelGrafo !== null) {
      // Incluye el cierre por falta de respuesta (ADR-0142): fila de
      // sistema, su nota explica días, ancla y recordatorios.
      kind = fueraDelGrafo.label;
      tone = "ev--neutral";
      // La nota del sintético repite el rótulo con otras palabras.
      if (fueraDelGrafo.kind === "migrated") text = null;
      // F8 — el pedido de nueva visita lleva el códec `motivo · detalle`:
      // se muestra el motivo con sus palabras, nunca la clave.
      if (fueraDelGrafo.kind === "visit_requested") {
        const pedido = decodeVisitRequestNote(ev.note);
        text = pedido
          ? [visitReasonLabelForAsset(pedido.reason, w.asset), pedido.note]
              .filter(Boolean)
              .join(" · ")
          : null;
        tone = "ev--crit";
      }
      // ADR-0161 — la respuesta del propietario a la visita propuesta (o
      // su confirmación automática). Sin nota: no se guarda texto suyo.
      if (fueraDelGrafo.kind === "visit_confirmed") tone = "ev--ok";
      if (fueraDelGrafo.kind === "visit_other_time_requested") tone = "ev--crit";
    } else if (
      ev.synthetic === true ||
      ev.eventType === "reported" ||
      ev.eventType === "postventa_owner_observation_registered"
    ) {
      kind = "Reportó la observación";
      const n = ev.evidenceCount ?? ctx.fotosDeNacimiento;
      text = n > 0 ? `Con ${n} ${n === 1 ? "foto" : "fotos"}.` : text;
    } else if (ev.eventType === "observation_conformed" && lane === "system") {
      // Un cierre de sistema jamás es una conformidad (ADR-0142 §3).
      kind = OBSERVATION_AUTO_CLOSED_LABEL;
      tone = "ev--neutral";
    } else if (ev.eventType === "observation_conformed") {
      kind = "Dio conformidad";
      tone = "ev--ok";
    } else if (ev.eventType === "observation_not_conformed") {
      ciclo += 1;
      kind = `Marcó no conforme · se abre el ciclo ${ciclo}`;
      tone = "ev--crit";
    } else if (
      ev.eventType === "classification_corrected" ||
      ev.eventType === "postventa_classification_corrected"
    ) {
      kind = "Clasificación corregida";
      tone = "ev--neutral";
    } else if (ev.eventType === "classification_review_resolved") {
      kind = "Clasificación elegida por el supervisor";
      tone = "ev--neutral";
    } else if (
      ev.eventType === "classification_reclassified" ||
      ev.eventType === "postventa_classification_reclassified" ||
      ev.eventType === "standalone_classification_adjusted"
    ) {
      kind = "Reclasificada con nueva evidencia · IA";
      tone = "ev--neutral";
    } else if (ev.eventType === "evidence_attached") {
      kind = "Adjuntó evidencia";
      tone = "ev--neutral";
    } else if (ev.eventType === "rejection_accepted") {
      // ADR-0141: el propietario aceptó el rechazo — cierra el carril.
      kind = "Aceptó el rechazo · queda cerrada";
      tone = "ev--neutral";
    } else if (ev.eventType === "rejection_appealed") {
      kind = "Pidió una segunda revisión del rechazo";
      tone = "ev--crit";
    } else if (ev.toStatus !== null) {
      const to = phaseOf(ev.toStatus);
      const from = phaseOf(ev.fromStatus);
      // ADR-0141: la resolución de una apelación se distingue por el
      // ORIGEN (un estado apelado, derivado del grafo — nunca una clave).
      const desdeApelada = ev.fromStatus !== null && apeladas.includes(ev.fromStatus);
      if (to === entryPhase && from === "awaiting_receiver") {
        ciclo += 1;
        kind = `Marcó no conforme · se abre el ciclo ${ciclo}`;
        tone = "ev--crit";
      } else if (desdeApelada && to === "awaiting_assignment") {
        kind = "Acogió la apelación · la observación vuelve a aceptada";
        tone = "ev--ok";
      } else if (desdeApelada && to === "closed") {
        kind = `Mantuvo el rechazo · mensaje al ${w.assetParty.toLowerCase()}`;
        tone = "ev--neutral";
      } else if (to === "awaiting_receiver_on_rejection") {
        // «Rechazada» → «Rechazó»: el verbo sale de la etiqueta del
        // destino, igual que el cierre de abajo (ADR-0141: el rechazo ya
        // no es un cierre — espera la respuesta del propietario).
        const destino = ctx.vocab.observationStatuses.find((s) => s.key === ev.toStatus);
        const pasado = destino?.label.replace(/ada$/, "ó").replace(/ida$/, "ió");
        kind =
          pasado && pasado !== destino?.label
            ? `${pasado} · mensaje al ${w.assetParty.toLowerCase()}`
            : "Rechazó la observación";
        tone = "ev--neutral";
      } else if (to === "awaiting_assignment" && to !== entryPhase) {
        kind = "Aceptó la observación";
      } else if (to === "with_assignee") {
        kind = `Asignó a ${asignadoDelHito} · instrucciones`;
        tone = "ev--assign";
      } else if (to === "awaiting_receiver") {
        kind = "Marcó como resuelta";
      } else if (
        to === "closed" &&
        from === "awaiting_receiver_on_rejection" &&
        lane === "system"
      ) {
        // El cierre administrativo del cutover (migración 0066, actor
        // sistema): nadie envió un mensaje al propietario, así que no
        // lleva la coletilla; la nota de la migración explica el salto.
        kind = "Rechazo cerrado · cierre administrativo";
        tone = "ev--neutral";
      } else if (to === "closed") {
        // «Rechazada» → «Rechazó», «Anulada» → «Anuló»: el verbo sale de
        // la etiqueta del destino, no de su clave.
        const destino = ctx.vocab.observationStatuses.find((s) => s.key === ev.toStatus);
        const pasado = destino?.label.replace(/ada$/, "ó").replace(/ida$/, "ió");
        kind =
          pasado && pasado !== destino?.label
            ? `${pasado} · mensaje al ${w.assetParty.toLowerCase()}`
            : "Cerró la observación";
        tone = "ev--neutral";
      } else {
        kind = "Cambió el estado";
        tone = "ev--neutral";
      }
    } else {
      kind = "Nota";
      tone = "ev--neutral";
    }

    return { id: ev.id, occurredAt: ev.occurredAt, author: who, role, roleClass, kind, text, tone };
  });
}

/** La clave con que nace una observación en esta receta (la primera de
 *  la fase de entrada), para saber qué fase cuenta como «volver al
 *  principio». */
function presentationalStateEntryKey(vocab: ObservationPhaseVocabulary): string | null {
  return observationEntryStatus(vocab);
}
